/**
 * oceanLogApi.js — the four reads behind the Ocean API Logs screen.
 *
 * Backend: routes/oceanQueryLog.route.js, mounted at /api/ocean-query-log
 * (server.js). `config.apiBaseUrl` already ends in /api/, so paths here are
 * relative to that.
 *
 * Every read takes the same filter set — queryId, userId, type, search, from,
 * to, shippingLine, failedOnly — which is why the table, the stat cards and the
 * user tab all narrow together.
 */
import { httpinstance } from "../../../axios/api";

const BASE = "ocean-query-log";

/** The pager's page sizes; the largest is the backend's MAX_GROUP_LIMIT. */
export const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
export const DEFAULT_PAGE_SIZE = 10;

/** Unwraps ApiResponse (`{ statusCode, data, message, success }`). */
const unwrap = (response) => response?.data?.data ?? null;

/** Drops empty filters so they never reach the query string as `?type=`. */
const toParams = (filters = {}) =>
  Object.entries(filters).reduce((params, [key, value]) => {
    if (value !== null && value !== undefined && value !== "" && value !== "all") params[key] = value;
    return params;
  }, {});

/**
 * GET /?groupBy=query — one entry per query with its attempts nested.
 *
 * `page` / `limit` page the QUERIES: the server groups and pages, so one call
 * returns exactly the collapsed rows on screen with their attempts attached.
 * It used to fetch every matching row (limit=2000) and group in the browser,
 * which is what made this the heaviest read on the screen.
 *
 * @returns {Promise<{groups: Object[], pagination: Object}>}
 */
export const fetchGroupedLogs = async (filters = {}, { page = 1, limit = DEFAULT_PAGE_SIZE } = {}) => {
  const response = await httpinstance.get(BASE, {
    params: { ...toParams(filters), groupBy: "query", page, limit },
  });
  const payload = unwrap(response);
  return {
    groups: Array.isArray(payload?.data) ? payload.data : [],
    // `total` counts the matching QUERIES (what the pager pages through);
    // `totalLogs` counts the attempts inside them, for the line under the table.
    pagination: payload?.pagination || null,
  };
};

/** Page size used to walk the listing for an export, and the ceiling on it. */
const EXPORT_PAGE_SIZE = 100;
export const EXPORT_GROUP_LIMIT = 2000;

/**
 * Every group matching the filters, for the Excel export — the one place that
 * still needs more than a page.
 *
 * Walked a page at a time on purpose: the screen no longer pays for this, so
 * the heavy read happens once, on a click, instead of on every filter change.
 * Capped at EXPORT_GROUP_LIMIT queries; `truncated` says when the file is short
 * of the full match so the caller can tell the user rather than hand them a
 * silently partial download.
 *
 * @returns {Promise<{groups: Object[], total: Number, truncated: Boolean}>}
 */
export const fetchAllGroupedLogs = async (filters = {}) => {
  const groups = [];
  let page = 1;
  let total = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchGroupedLogs(filters, { page, limit: EXPORT_PAGE_SIZE });
    groups.push(...result.groups);
    total = result.pagination?.total ?? groups.length;

    const done = !result.groups.length || groups.length >= total || groups.length >= EXPORT_GROUP_LIMIT;
    if (done) break;
    page += 1;
  }

  return { groups, total, truncated: groups.length < total };
};

/**
 * GET /shipping-lines — options for the line dropdown, derived server-side from
 * a window of the newest matching rows. Selecting one filters everything.
 * @returns {Promise<Object[]>} [{ value, label, logo }]
 */
export const fetchShippingLines = async (filters = {}) => {
  const response = await httpinstance.get(`${BASE}/shipping-lines`, { params: toParams(filters) });
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : [];
};

/** GET /stats — the four stat cards. */
export const fetchLogStats = async (filters = {}) => {
  const response = await httpinstance.get(`${BASE}/stats`, { params: toParams(filters) });
  return unwrap(response) || {};
};

/** GET /user-counts — the User-wise Count tab (also feeds the user filter). */
export const fetchUserCounts = async (filters = {}) => {
  const response = await httpinstance.get(`${BASE}/user-counts`, { params: toParams(filters) });
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : [];
};

/** GET /query/:queryId — every attempt for one query, newest first. */
export const fetchLogsByQueryId = async (queryId) => {
  const response = await httpinstance.get(`${BASE}/query/${queryId}`);
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : [];
};

/**
 * GET /:id — a single row with its full `request`/`response`. The listing omits
 * those payloads (they were nearly all of its weight), so the detail modal
 * reads them from here for the one log being opened.
 */
export const fetchLogById = async (id) => {
  const response = await httpinstance.get(`${BASE}/${id}`);
  return unwrap(response);
};
